/**
 * Signed configuration: where CORE can be reached and which key it will
 * present next (docs/protocol/config-v1.md, SEC-ARCH-002).
 *
 * The one thing this module must get right is who may say it. Not CORE —
 * its compromise is the threat model — and not a relay, which is
 * disposable. The owner's offline key signs; the public key is compiled in
 * below; CORE only carries the bytes. A client with no embedded key runs
 * with the bootstrap list and nothing else, which is the rollback path.
 *
 * The signature covers the body AS TEXT. Verifiers in three languages
 * check the bytes first and parse afterwards, so no JSON canonicalisation
 * has to agree across them.
 */
import nacl from 'tweetnacl';

const CONTEXT = 'VoxioxConfigV1';
const SEP = '\x1e';
const enc = new TextEncoder();

/** Public keys allowed to sign configuration, base64. Two during a rotation. */
export const CONFIG_PUBKEYS = Object.freeze(['kawT+4l8xz1cvM27b1sXwtmxEFP2zWNiTkKsWkn8xeM=']);

/** Compiled-in paths to CORE, used until a signed list is held (and if no key is embedded). */
export const BOOTSTRAP = Object.freeze({
  core: { host: 'core.voxiox.com', spki: ['LNT7XEFRnd2eTZbM1tXiWIp2AOJi2TUvl8qRJuDCI8Y='] },
  endpoints: [
    { host: 'core.voxiox.com', ports: [443], proto: 'tls', direct: true },
    // tw: the first relay (SEC-ARCH-005) — reserved here, used from SEC-ARCH-006 on.
    { host: '5.129.203.232', ports: [443, 2053, 8443], proto: 'tls', name: 'tw', sni: 'tw.voxiox.com' },
  ],
});

export const KIND = Object.freeze({ RELAY_LIST: 'relay-list-v1', PIN_ROTATION: 'pin-rotation-v1', APP_RELEASE: 'app-release-v1' });
export const CLOCK_SKEW_S = 300;

export const CONFIG_ERROR = Object.freeze({
  DISABLED: 'disabled',           // no embedded key — the mechanism is off
  MALFORMED: 'malformed',
  UNKNOWN_KEY: 'unknown-key',     // kid is not one we carry
  BAD_SIGNATURE: 'bad-signature',
  WRONG_KIND: 'wrong-kind',
  NOT_YET_VALID: 'not-yet-valid',
  EXPIRED: 'expired',
  ROLLBACK: 'rollback',
  FORKED: 'forked',
  BAD_BODY: 'bad-body',
});

const b64u8 = (u8) => btoa(String.fromCharCode(...u8));
function unb64(s) {
  try {
    const bin = atob(String(s));
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch (e) { return null; }
}

/** The exact bytes under the signature. */
export function signingBytes(kind, bodyText) {
  return enc.encode(`${CONTEXT}${SEP}${kind}${SEP}${bodyText}`);
}

/** Owner-side: an envelope over the body text. */
export function signConfig(bodyText, kind, secretKey) {
  const kp = nacl.sign.keyPair.fromSecretKey(secretKey);
  return {
    v: 1, kind, body: bodyText,
    sig: b64u8(nacl.sign.detached(signingBytes(kind, bodyText), secretKey)),
    kid: b64u8(kp.publicKey),
  };
}

/** Short hash of the body text, to tell a fork from a duplicate. */
export function bodyHash(bodyText) {
  return b64u8(nacl.hash(enc.encode(bodyText)).slice(0, 16));
}

const isB64Key = (s) => typeof s === 'string' && (unb64(s) || []).length === 32;
const isPort = (p) => Number.isInteger(p) && p >= 1 && p <= 65535;
const isHost = (h) => typeof h === 'string' && /^[A-Za-z0-9.\-:\[\]]{1,253}$/.test(h);

function checkBody(kind, body) {
  if (!body || typeof body !== 'object') return CONFIG_ERROR.BAD_BODY;
  if (!Number.isInteger(body.version) || body.version < 1) return CONFIG_ERROR.BAD_BODY;
  if (!Number.isInteger(body.issued) || !Number.isInteger(body.expires) || body.expires <= body.issued) return CONFIG_ERROR.BAD_BODY;
  if (body.previous_version != null && !(Number.isInteger(body.previous_version) && body.previous_version < body.version)) return CONFIG_ERROR.BAD_BODY;
  if (kind === KIND.RELAY_LIST) {
    if (!body.core || !isHost(body.core.host)) return CONFIG_ERROR.BAD_BODY;
    if (!Array.isArray(body.core.spki) || body.core.spki.length < 1 || !body.core.spki.every(isB64Key)) return CONFIG_ERROR.BAD_BODY;
    if (!Array.isArray(body.endpoints) || body.endpoints.length < 1) return CONFIG_ERROR.BAD_BODY;
    for (const e of body.endpoints) {
      if (!e || !isHost(e.host) || !Array.isArray(e.ports) || e.ports.length < 1 || !e.ports.every(isPort)) return CONFIG_ERROR.BAD_BODY;
      if (e.proto !== 'tls') return CONFIG_ERROR.BAD_BODY;
    }
    if (!body.endpoints.some(e => e.direct === true)) return CONFIG_ERROR.BAD_BODY;
  } else if (kind === KIND.APP_RELEASE) {
    // Which build the phones should run. The URL is a PATH on CORE: an
    // address here would turn the owner's signature into a way to send a
    // phone somewhere else. The hash is what makes the file trustworthy, so
    // its shape is checked before anything downloads.
    const a = body.android;
    if (!a || typeof a !== 'object') return CONFIG_ERROR.BAD_BODY;
    if (!Number.isInteger(a.versionCode) || a.versionCode < 0) return CONFIG_ERROR.BAD_BODY;
    if (typeof a.versionName !== 'string') return CONFIG_ERROR.BAD_BODY;
    if (typeof a.url !== 'string' || typeof a.sha256 !== 'string' || !Number.isInteger(a.size)) return CONFIG_ERROR.BAD_BODY;
    const published = a.versionCode > 0 || a.url !== '' || a.sha256 !== '';
    if (published) {
      if (!a.url.startsWith('/') || a.url.startsWith('//') || a.url.includes('..')) return CONFIG_ERROR.BAD_BODY;
      if (!/^[0-9a-f]{64}$/.test(a.sha256)) return CONFIG_ERROR.BAD_BODY;
      if (a.size <= 0 || a.versionCode <= 0) return CONFIG_ERROR.BAD_BODY;
    }
  } else if (kind === KIND.PIN_ROTATION) {
    if (!isHost(body.host)) return CONFIG_ERROR.BAD_BODY;
    if (!body.spki || !isB64Key(body.spki.current) || (body.spki.next != null && !isB64Key(body.spki.next))) return CONFIG_ERROR.BAD_BODY;
  } else {
    return CONFIG_ERROR.WRONG_KIND;
  }
  return null;
}

/**
 * The five checks of config-v1.md §Verification, in that order.
 *
 * `held` is `{ version, hash }` of the configuration of this kind we already
 * accepted, or null. Returns `{ ok, body, version, hash }` or `{ ok:false, error }`.
 */
export function verifyConfig(envelope, { pubkeys = CONFIG_PUBKEYS, kind, held = null, now = Math.floor(Date.now() / 1000) } = {}) {
  if (!pubkeys || pubkeys.length === 0) return { ok: false, error: CONFIG_ERROR.DISABLED };
  if (!envelope || envelope.v !== 1 || typeof envelope.body !== 'string'
      || typeof envelope.sig !== 'string' || typeof envelope.kid !== 'string' || typeof envelope.kind !== 'string') {
    return { ok: false, error: CONFIG_ERROR.MALFORMED };
  }
  if (kind && envelope.kind !== kind) return { ok: false, error: CONFIG_ERROR.WRONG_KIND };
  if (!pubkeys.includes(envelope.kid)) return { ok: false, error: CONFIG_ERROR.UNKNOWN_KEY };
  const pub = unb64(envelope.kid), sig = unb64(envelope.sig);
  if (!pub || pub.length !== 32 || !sig || sig.length !== 64) return { ok: false, error: CONFIG_ERROR.MALFORMED };
  let good = false;
  try { good = nacl.sign.detached.verify(signingBytes(envelope.kind, envelope.body), sig, pub); } catch (e) { good = false; }
  if (!good) return { ok: false, error: CONFIG_ERROR.BAD_SIGNATURE };

  let body;
  try { body = JSON.parse(envelope.body); } catch (e) { return { ok: false, error: CONFIG_ERROR.BAD_BODY }; }
  if (!body || body.kind !== envelope.kind) return { ok: false, error: CONFIG_ERROR.WRONG_KIND };
  const shape = checkBody(envelope.kind, body);
  if (shape) return { ok: false, error: shape };
  if (body.issued > now + CLOCK_SKEW_S) return { ok: false, error: CONFIG_ERROR.NOT_YET_VALID };
  if (body.expires <= now) return { ok: false, error: CONFIG_ERROR.EXPIRED };

  const hash = bodyHash(envelope.body);
  if (held) {
    if (body.version < held.version) return { ok: false, error: CONFIG_ERROR.ROLLBACK, held };
    if (body.version === held.version) {
      return hash === held.hash ? { ok: true, body, version: body.version, hash, unchanged: true }
        : { ok: false, error: CONFIG_ERROR.FORKED, held };
    }
  }
  return { ok: true, body, version: body.version, hash };
}

// ─── Held configuration ────────────────────────────────────────────────────

const key = (kind) => `voxiox_config:${kind}`;

export function heldConfig(storage, kind) {
  try {
    const raw = storage.getItem(key(kind));
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && Number.isInteger(v.version) && typeof v.hash === 'string' && v.envelope ? v : null;
  } catch (e) { return null; }
}

/**
 * Verify against what is held and keep it if it passes. A refusal keeps the
 * held configuration and says why — the caller shows the warning.
 */
export function adoptConfig(storage, envelope, { pubkeys = CONFIG_PUBKEYS, kind, now } = {}) {
  const held = heldConfig(storage, kind);
  const r = verifyConfig(envelope, { pubkeys, kind, now, held: held ? { version: held.version, hash: held.hash } : null });
  if (!r.ok) return { ...r, held };
  if (r.unchanged) return { ...r, held, adopted: false };
  const next = { version: r.version, hash: r.hash, envelope, adoptedAt: now ?? Math.floor(Date.now() / 1000) };
  storage.setItem(key(kind), JSON.stringify(next));
  return { ...r, held: next, adopted: true };
}

/**
 * What the client should use right now: the held signed list if it is still
 * within its validity, else the bootstrap. Expiry is re-checked at use, not
 * only at adoption — a list adopted a month ago may have run out since.
 */
export function currentRelayList(storage, { now = Math.floor(Date.now() / 1000) } = {}) {
  const held = heldConfig(storage, KIND.RELAY_LIST);
  if (held) {
    try {
      const body = JSON.parse(held.envelope.body);
      if (body.expires > now) return { source: 'signed', version: held.version, core: body.core, endpoints: body.endpoints, expires: body.expires };
    } catch (e) { /* fall through to bootstrap */ }
  }
  return { source: 'bootstrap', version: 0, core: BOOTSTRAP.core, endpoints: BOOTSTRAP.endpoints };
}

/** Fetch from CORE (through any path) and adopt. Network errors keep what is held. */
export async function refreshConfig(api, storage, kind, opts = {}) {
  let envelope;
  try { envelope = await api.getConfig(kind); } catch (e) { return { ok: false, error: 'unreachable', detail: e && e.message, held: heldConfig(storage, kind) }; }
  if (!envelope) return { ok: false, error: 'none-published', held: heldConfig(storage, kind) };
  return adoptConfig(storage, envelope, { ...opts, kind });
}
